/**
 * Customer Account API OAuth (PKCE) for storefront / App WebView.
 * Requires Headless client_id in theme settings and /pages/user-center registered in Headless
 * as the OAuth callback URI (legacy /pages/auth-callback still works if configured).
 *
 * Endpoints are hardcoded for Offhoo (account.offhoo.com) to avoid discovery round-trips.
 */
(function (global) {
  const STORAGE = {
    accessToken: 'offhoo_ca_access_token',
    expiresAt: 'offhoo_ca_expires_at',
    customerId: 'offhoo_ca_customer_id',
    idToken: 'offhoo_ca_id_token',
    refreshToken: 'offhoo_ca_refresh_token',
    profileCache: 'offhoo_ca_profile_cache',
  };

  const SESSION = {
    verifier: 'offhoo_ca_code_verifier',
    state: 'offhoo_ca_oauth_state',
    returnTo: 'offhoo_ca_return_to',
    profileHydrated: 'offhoo_ca_profile_hydrated',
  };

  /** Offhoo Customer Account hosts — skip /.well-known discovery. */
  const ENDPOINTS = {
    authorization: 'https://account.offhoo.com/authentication/oauth/authorize',
    token: 'https://account.offhoo.com/authentication/oauth/token',
    endSession: 'https://account.offhoo.com/authentication/logout',
    graphql: 'https://account.offhoo.com/customer/api/2026-07/graphql',
  };

  const CUSTOMER_QUERY = `
    query OffhooCustomerAccountCustomer {
      customer {
        id
        displayName
        firstName
        lastName
        emailAddress {
          emailAddress
        }
      }
    }
  `;

  const root = global.OffhooCustomerOAuth || (global.OffhooCustomerOAuth = {});

  function getConfig() {
    return global.OffhooCustomerOAuthConfig || {};
  }

  function isEnabled() {
    const cfg = getConfig();
    return Boolean(cfg.enabled && cfg.clientId);
  }

  function getRedirectUri() {
    const cfg = getConfig();
    const path = cfg.redirectPath || '/pages/user-center';
    return `${global.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }

  function getLogoutRedirectUri(overridePath) {
    const cfg = getConfig();
    const path = overridePath || cfg.logoutPath || '/pages/user-center';
    if (/^https?:\/\//i.test(path)) return path;
    return `${global.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }

  function base64UrlEncode(buffer) {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function randomVerifier() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  }

  async function sha256Base64Url(value) {
    const data = new TextEncoder().encode(value);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(hash));
  }

  function randomState() {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  }

  root.getEndpoints = function getEndpoints() {
    return { ...ENDPOINTS };
  };

  root.hasValidToken = function hasValidToken() {
    try {
      const token = localStorage.getItem(STORAGE.accessToken);
      const exp = Number(localStorage.getItem(STORAGE.expiresAt) || 0);
      if (!token) return false;
      return Date.now() < exp - 60000;
    } catch (_e) {
      return false;
    }
  };

  root.getAccessToken = function getAccessToken() {
    if (!root.hasValidToken()) return null;
    try {
      return localStorage.getItem(STORAGE.accessToken);
    } catch (_e) {
      return null;
    }
  };

  root.clearSession = function clearSession() {
    try {
      localStorage.removeItem(STORAGE.accessToken);
      localStorage.removeItem(STORAGE.expiresAt);
      localStorage.removeItem(STORAGE.customerId);
      localStorage.removeItem(STORAGE.idToken);
      localStorage.removeItem(STORAGE.refreshToken);
      localStorage.removeItem(STORAGE.profileCache);
      sessionStorage.removeItem(SESSION.profileHydrated);
    } catch (_e) {
      /* ignore */
    }
    notifyAuthState(false);
  };

  /**
   * @param {string} accessToken
   * @param {number} [expiresInSeconds]
   * @param {string} [idToken]
   * @param {string} [refreshToken]
   * @param {{ silent?: boolean }} [options]
   */
  root.storeToken = function storeToken(accessToken, expiresInSeconds, idToken, refreshToken, options) {
    const ttl = Number(expiresInSeconds) || 86400;
    const silent = Boolean(options && options.silent);
    try {
      localStorage.setItem(STORAGE.accessToken, accessToken);
      localStorage.setItem(STORAGE.expiresAt, String(Date.now() + ttl * 1000));
      if (idToken) localStorage.setItem(STORAGE.idToken, idToken);
      if (refreshToken) localStorage.setItem(STORAGE.refreshToken, refreshToken);
    } catch (_e) {
      /* ignore */
    }
    if (!silent) notifyAuthState(true, accessToken);
  };

  root.getRefreshToken = function getRefreshToken() {
    try {
      return localStorage.getItem(STORAGE.refreshToken) || null;
    } catch (_e) {
      return null;
    }
  };

  function normalizeCustomerId(rawId) {
    if (!rawId) return '';
    const value = String(rawId);
    const match = value.match(/Customer\/(\d+)/i);
    return match ? match[1] : value;
  }

  function customerEmail(customer) {
    return (customer?.emailAddress?.emailAddress || '').trim();
  }

  function displayNameFromCustomer(customer) {
    if (!customer) return '';
    const email = customerEmail(customer);
    const parts = [(customer.firstName || '').trim(), (customer.lastName || '').trim()].filter(Boolean);
    if (parts.length) return parts.join(' ');
    const display = (customer.displayName || '').trim();
    if (display && (!email || display.toLowerCase() !== email.toLowerCase())) return display;
    return '';
  }

  function initialsFromCustomer(customer) {
    if (!customer) return '';
    const first = (customer.firstName || '').trim();
    const last = (customer.lastName || '').trim();
    if (first || last) return (first.slice(0, 1) + last.slice(0, 1)).toUpperCase() || '';
    const email = customerEmail(customer);
    const display = (customer.displayName || '').trim();
    if (display && (!email || display.toLowerCase() !== email.toLowerCase())) {
      const parts = display.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return display.slice(0, 2).toUpperCase();
    }
    if (email) return email.slice(0, 2).toUpperCase();
    return '';
  }

  function writeProfileCache(customer) {
    try {
      const name = displayNameFromCustomer(customer);
      const email = customerEmail(customer);
      localStorage.setItem(
        STORAGE.profileCache,
        JSON.stringify({
          name: name || email || '',
          email: email || '',
          initials: initialsFromCustomer(customer),
        })
      );
      sessionStorage.setItem(SESSION.profileHydrated, '1');
    } catch (_e) {
      /* ignore */
    }
  }

  /** Single Customer Account GraphQL call (id + profile). */
  root.fetchCustomer = async function fetchCustomer(accessToken) {
    const token = accessToken || root.getAccessToken();
    if (!token) return null;

    const res = await fetch(ENDPOINTS.graphql, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({ query: CUSTOMER_QUERY }),
    });

    if (!res.ok) {
      const err = new Error(`Customer request failed (${res.status})`);
      err.status = res.status;
      err.authFailed = res.status === 401 || res.status === 403;
      throw err;
    }

    const json = await res.json().catch(() => ({}));
    if (json.errors?.length) {
      const err = new Error(json.errors[0]?.message || 'Customer query failed');
      err.authFailed = true;
      throw err;
    }

    return json.data?.customer || null;
  };

  root.refreshAccessToken = async function refreshAccessToken() {
    const cfg = getConfig();
    const refreshToken = root.getRefreshToken();
    if (!refreshToken || !cfg.clientId) return false;

    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: cfg.clientId,
        refresh_token: refreshToken,
      });

      const tokenRes = await fetch(ENDPOINTS.token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const tokenJson = await tokenRes.json().catch(() => ({}));
      console.log('[offhoo-customer-oauth] refresh token response', tokenJson);

      if (!tokenRes.ok || !tokenJson.access_token) {
        console.warn(
          '[offhoo-customer-oauth] refresh failed:',
          tokenJson.error_description || tokenJson.error || tokenRes.status
        );
        return false;
      }

      root.storeToken(
        tokenJson.access_token,
        tokenJson.expires_in,
        tokenJson.id_token,
        tokenJson.refresh_token || refreshToken,
        { silent: true }
      );
      try {
        await root.notifyAccountToApp({
          accessToken: tokenJson.access_token,
          hydrateProfile: true,
        });
      } catch (err) {
        console.warn('[offhoo-customer-oauth] notify account after refresh failed:', err);
      }
      notifyAuthState(true, tokenJson.access_token);
      return true;
    } catch (err) {
      console.warn('[offhoo-customer-oauth] refreshAccessToken error:', err);
      return false;
    }
  };

  root.ensureValidToken = async function ensureValidToken() {
    if (root.hasValidToken()) return root.getAccessToken();
    const refreshed = await root.refreshAccessToken();
    if (refreshed && root.hasValidToken()) return root.getAccessToken();
    return null;
  };

  /**
   * Notify App + optionally hydrate customer id/profile in one GraphQL.
   * @param {{ accessToken?: string, customerId?: string, expired?: string|number, hydrateProfile?: boolean }} [opts]
   */
  root.notifyAccountToApp = async function notifyAccountToApp(opts = {}) {
    const accessToken = opts.accessToken || root.getAccessToken();
    if (!accessToken) return false;

    let expired = opts.expired;
    if (expired == null) {
      try {
        expired = localStorage.getItem(STORAGE.expiresAt) || '';
      } catch (_e) {
        expired = '';
      }
    }

    let customerId = opts.customerId;
    if (!customerId) {
      try {
        customerId = localStorage.getItem(STORAGE.customerId) || '';
      } catch (_e) {
        customerId = '';
      }
    }

    if (!customerId || opts.hydrateProfile) {
      try {
        const customer = await root.fetchCustomer(accessToken);
        if (customer) {
          customerId = normalizeCustomerId(customer.id) || customerId;
          if (customerId) {
            try {
              localStorage.setItem(STORAGE.customerId, customerId);
            } catch (_e) {
              /* ignore */
            }
          }
          writeProfileCache(customer);
        }
      } catch (err) {
        console.warn('[offhoo-customer-oauth] fetch customer failed:', err);
      }
    }

    const accountMsg = {
      id: Date.now(),
      type: 'ACCOUNT',
      action: 'getAccessToken',
      params: {
        access_token: accessToken,
        customer_id: customerId || '',
        expired: String(expired || ''),
      },
    };

    console.log('messageChannel_accountMsg', JSON.stringify(accountMsg));

    try {
      if (global.OffhooApp?.postMessageChannel) {
        return Boolean(global.OffhooApp.postMessageChannel(accountMsg));
      }
      if (global.flutter_inappwebview?.callHandler) {
        global.flutter_inappwebview.callHandler('messageChannel', JSON.stringify(accountMsg));
        return true;
      }
      if (global.OffhooAppBridge?.postMessage) {
        global.OffhooAppBridge.postMessage(JSON.stringify(accountMsg));
        return true;
      }
      if (global.webkit?.messageHandlers?.messageChannel) {
        global.webkit.messageHandlers.messageChannel.postMessage(accountMsg);
        return true;
      }
    } catch (err) {
      console.warn('[offhoo-customer-oauth] notifyAccountToApp failed:', err);
    }
    return false;
  };

  function notifyAuthState(loggedIn, accessToken) {
    const payload = { type: 'offhoo:auth', loggedIn: Boolean(loggedIn) };
    if (loggedIn && accessToken) payload.accessToken = accessToken;

    try {
      if (global.OffhooAppBridge?.postMessage) {
        global.OffhooAppBridge.postMessage(JSON.stringify(payload));
      } else if (global.webkit?.messageHandlers?.offhooApp) {
        global.webkit.messageHandlers.offhooApp.postMessage(payload);
      } else if (global.flutter_inappwebview?.callHandler) {
        global.flutter_inappwebview.callHandler('offhooApp', payload);
      }
    } catch (_e) {
      /* ignore */
    }

    try {
      global.dispatchEvent(new CustomEvent('offhoo:auth', { detail: payload }));
    } catch (_e) {
      /* ignore */
    }
  }

  root.startLogin = async function startLogin(returnTo) {
    const cfg = getConfig();
    if (!isEnabled()) {
      global.location.href = cfg.loginUrl || cfg.profileUrl || '/';
      return;
    }

    const verifier = randomVerifier();
    const challenge = await sha256Base64Url(verifier);
    const state = randomState();
    const destination = returnTo || cfg.profileUrl || global.location.href;

    try {
      sessionStorage.setItem(SESSION.verifier, verifier);
      sessionStorage.setItem(SESSION.state, state);
      sessionStorage.setItem(SESSION.returnTo, destination);
    } catch (_e) {
      /* continue */
    }

    const url = new URL(ENDPOINTS.authorization);
    url.searchParams.set('client_id', cfg.clientId);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('redirect_uri', getRedirectUri());
    url.searchParams.set('scope', cfg.scope || 'openid email customer-account-api:full');
    url.searchParams.set('state', state);
    url.searchParams.set('code_challenge', challenge);
    url.searchParams.set('code_challenge_method', 'S256');
    if (cfg.locale) url.searchParams.set('locale', cfg.locale);
    if (cfg.regionCountry) url.searchParams.set('region_country', cfg.regionCountry);
    global.location.assign(url.toString());
  };

  root.handleCallback = async function handleCallback() {
    const cfg = getConfig();
    const params = new URLSearchParams(global.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    if (error) throw new Error(errorDescription || error);

    const code = params.get('code');
    const state = params.get('state');
    if (!code) throw new Error('Missing authorization code');

    let expectedState = null;
    let verifier = null;
    let returnTo = '/pages/user-center';
    try {
      expectedState = sessionStorage.getItem(SESSION.state);
      verifier = sessionStorage.getItem(SESSION.verifier);
      const storedReturn = sessionStorage.getItem(SESSION.returnTo);
      if (storedReturn) returnTo = storedReturn;
      sessionStorage.removeItem(SESSION.state);
      sessionStorage.removeItem(SESSION.verifier);
      sessionStorage.removeItem(SESSION.returnTo);
    } catch (_e) {
      /* ignore */
    }

    if (!verifier) throw new Error('Missing PKCE verifier. Please try logging in again.');
    if (!state || state !== expectedState) throw new Error('Invalid OAuth state.');

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cfg.clientId,
      code,
      redirect_uri: getRedirectUri(),
      code_verifier: verifier,
    });

    const tokenRes = await fetch(ENDPOINTS.token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    const tokenJson = await tokenRes.json().catch(() => ({}));
    console.log('[offhoo-customer-oauth] code→token response', tokenJson);
    console.log('[offhoo-customer-oauth] code→token summary', {
      keys: Object.keys(tokenJson || {}),
      token_type: tokenJson.token_type,
      expires_in: tokenJson.expires_in,
      has_access_token: Boolean(tokenJson.access_token),
      has_id_token: Boolean(tokenJson.id_token),
      has_refresh_token: Boolean(tokenJson.refresh_token),
      error: tokenJson.error,
      error_description: tokenJson.error_description,
    });

    if (!tokenRes.ok) {
      throw new Error(
        tokenJson.error_description || tokenJson.error || `Token exchange failed (${tokenRes.status})`
      );
    }
    if (!tokenJson.access_token) throw new Error('No access token in response');

    // Silent store → one GraphQL hydrate → auth event (user-center skips duplicate fetch).
    root.storeToken(
      tokenJson.access_token,
      tokenJson.expires_in,
      tokenJson.id_token,
      tokenJson.refresh_token,
      { silent: true }
    );
    try {
      await root.notifyAccountToApp({
        accessToken: tokenJson.access_token,
        hydrateProfile: true,
      });
    } catch (err) {
      console.warn('[offhoo-customer-oauth] notify account after login failed:', err);
    }
    notifyAuthState(true, tokenJson.access_token);

    const currentPath = (global.location.pathname || '/').replace(/\/$/, '') || '/';
    let destPath = '/pages/user-center';
    try {
      destPath = new URL(returnTo, global.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (_e) {
      /* keep default */
    }

    if (destPath === currentPath) {
      try {
        const url = new URL(global.location.href);
        ['code', 'state', 'error', 'error_description'].forEach((key) => url.searchParams.delete(key));
        const qs = url.searchParams.toString();
        global.history.replaceState(null, '', `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`);
      } catch (_e) {
        /* ignore */
      }
      return;
    }

    global.location.replace(returnTo);
  };

  /**
   * Clear local session, then end Shopify hosted session when id_token exists.
   * Does not notify the App. Does not reload user-center when there is no id_token.
   * @param {string} [returnTo] post_logout_redirect_uri path/url (Logout URI must allow it)
   * @returns {boolean} true if navigating to Shopify end_session
   */
  root.logout = function logout(returnTo) {
    let idToken = '';
    try {
      idToken = localStorage.getItem(STORAGE.idToken) || '';
    } catch (_e) {
      /* ignore */
    }

    root.clearSession();
    if (!idToken) return false;

    const postLogoutRedirectUri = getLogoutRedirectUri(returnTo);
    try {
      const logoutUrl = new URL(ENDPOINTS.endSession);
      logoutUrl.searchParams.set('id_token_hint', idToken);
      logoutUrl.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri);
      global.location.assign(logoutUrl.toString());
      return true;
    } catch (err) {
      console.warn('[offhoo-customer-oauth] logout redirect failed:', err);
      return false;
    }
  };

  root.verifyToken = async function verifyToken() {
    const token = root.getAccessToken();
    if (!token) return false;
    try {
      const customer = await root.fetchCustomer(token);
      if (!customer?.id) {
        root.clearSession();
        return false;
      }
      return true;
    } catch (_e) {
      return root.hasValidToken();
    }
  };

  function isAccountAuthUrl(href) {
    if (!href) return false;
    try {
      const url = new URL(href, global.location.origin);
      if (url.origin !== global.location.origin) return false;
      const path = url.pathname.toLowerCase();
      return path.includes('/account/login') || path.includes('/authentication/login');
    } catch (_e) {
      return false;
    }
  }

  function resolveCustomerAccountOrdersUrl(candidate, cfg) {
    const preferred = cfg?.ordersUrl || 'https://account.offhoo.com/orders';
    try {
      // Prefer hosted account domain; never keep storefront /account/orders.
      const url = new URL(candidate || preferred, global.location.origin);
      if (/account\.offhoo\.com$/i.test(url.hostname)) {
        url.pathname = '/orders';
        return url.toString();
      }
    } catch (_e) {
      /* fall through */
    }
    return preferred;
  }

  function onAccountTriggerClick(event) {
    const trigger = event.target.closest('[data-offhoo-account-trigger], [data-offhoo-login-trigger]');
    let link = trigger;

    if (!link) {
      const anchor = event.target.closest('a[href]');
      if (anchor && isAccountAuthUrl(anchor.href)) link = anchor;
    }

    if (!link) return;
    if (event.target.closest('[data-offhoo-open-settings]')) return;

    if (link.hasAttribute('data-offhoo-open-mine-tab') && global.OffhooApp?.isInApp?.()) {
      event.preventDefault();
      event.stopPropagation();
      if (global.OffhooApp.openMineTab) global.OffhooApp.openMineTab();
      return;
    }

    // Storefront / original login: do not intercept — native href / login URL.
    if (!isEnabled()) return;

    event.preventDefault();
    event.stopPropagation();

    const cfg = getConfig();
    let returnTo =
      link.getAttribute('data-offhoo-return-to') ||
      link.getAttribute('href') ||
      cfg.profileUrl ||
      global.location.href;

    if (link.hasAttribute('data-offhoo-user-center-orders-link')) {
      returnTo =
        link.getAttribute('data-offhoo-return-to') ||
        cfg.ordersUrl ||
        link.getAttribute('href') ||
        returnTo;
      returnTo = resolveCustomerAccountOrdersUrl(returnTo, cfg);
    }

    const goDest = (dest) => {
      const resolved = dest.startsWith('http') ? dest : new URL(dest, global.location.origin).href;
      if (resolved === '#' || resolved.endsWith('#offhoo-login')) {
        global.location.href = cfg.profileUrl || '/pages/user-center';
      } else {
        global.location.href = resolved;
      }
    };

    (async () => {
      if (root.hasValidToken()) {
        goDest(returnTo);
        return;
      }
      const token = await root.ensureValidToken();
      if (token) {
        goDest(returnTo.startsWith('#') ? cfg.profileUrl || '/pages/user-center' : returnTo);
        return;
      }
      root.startLogin(returnTo.startsWith('#') ? cfg.profileUrl || '/pages/user-center' : returnTo);
    })();
  }

  function shouldRunCallback() {
    const params = new URLSearchParams(global.location.search);
    if (!params.get('code') && !params.get('error')) return false;

    const cfg = getConfig();
    const path = cfg.redirectPath || '/pages/user-center';
    const current = global.location.pathname.replace(/\/$/, '');
    const expected = path.replace(/\/$/, '');
    return (
      current === expected ||
      current.endsWith('/user-center') ||
      current.endsWith('/auth-callback')
    );
  }

  async function boot() {
    document.addEventListener('click', onAccountTriggerClick, true);

    if (!isEnabled()) return;

    if (shouldRunCallback()) {
      try {
        await root.handleCallback();
      } catch (err) {
        console.error('[offhoo-customer-oauth] callback failed:', err);
        const el = document.querySelector('[data-offhoo-oauth-callback-error]');
        if (el) {
          el.hidden = false;
          el.textContent = err?.message || 'Login failed';
        }
        const loading = document.querySelector('[data-offhoo-oauth-callback-loading]');
        if (loading) loading.hidden = true;
      }
    } else if (root.hasValidToken()) {
      notifyAuthState(true, root.getAccessToken());
    } else if (root.getRefreshToken()) {
      await root.ensureValidToken();
    }
  }

  if (global.document?.readyState === 'loading') {
    global.document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : globalThis);
